import { stripAssigneeTag } from '../api/assignments';

const LEADING_TAG = /^\s*\([A-Z][A-Z\-/]{0,5}\)\s*/;

// Cleans up a recurring series title to a consistent standard:
//  - strips every existing (ID) assignee tag, leading AND trailing, however
//    many have accumulated from repeated re-tagging
//  - strips a manually-typed leading "Name - " / "Name: " / "Name — " prefix
//    matching any registered care partner, child, or sitter's full name,
//    short name, first name, or identifier — but only when followed by a
//    separator, so a real title like "Sam Haduko" (no separator after "Sam")
//    is never mistaken for a redundant "Sam - " prefix
//  - title-cases the result, but ONLY when it has no uppercase letters at all
//    (avoids mangling titles with deliberate mixed case or acronyms — this is
//    a known limitation for compound-cased words like "JiuJitsu", which the
//    preview step surfaces before anything is written, so it's visible, not silent)
//  - resolves a leaked raw sitter doc id (e.g. "sitter_1773448361726") back to
//    that sitter's short identifier before re-appending the tag, repairing
//    already-broken titles during the same cleanup pass
//  - re-appends exactly one trailing assignee tag, if any
export function standardizeTitle(rawTitle, { carePartners = [], children = [], sitters = [], assigneeCode = null } = {}) {
  let t = (rawTitle || '').trim();

  while (LEADING_TAG.test(t)) t = t.replace(LEADING_TAG, '').trim();
  t = stripAssigneeTag(t);

  const nameCandidates = [
    ...carePartners.flatMap(cp => [cp.name, cp.shortName, cp.name?.split(' ')[0], cp.identifier]),
    ...children.flatMap(c => [c.preferred_name, c.firstName]),
    ...sitters.flatMap(s => [s.firstName, s.identifier]),
  ].filter(Boolean);

  for (const n of nameCandidates) {
    const re = new RegExp(`^${n}\\s*[-:—–]\\s*`, 'i');
    if (re.test(t)) { t = t.replace(re, '').trim(); break; }
  }

  if (t && t === t.toLowerCase()) {
    t = t.split(' ').map(w => w ? w[0].toUpperCase() + w.slice(1) : w).join(' ');
  }

  // A previously-leaked raw sitter id in assigneeCode gets repaired here —
  // resolve it to the sitter's short identifier instead of re-embedding it.
  const leakedSitter = sitters.find(s => s.id === assigneeCode);
  const resolvedCode = leakedSitter ? (leakedSitter.identifier || leakedSitter.firstName?.[0]?.toUpperCase()) : assigneeCode;

  return resolvedCode ? `${t} (${resolvedCode})` : t;
}
